import { KnowledgeMarkdown } from '@/components/features/KnowledgeBase/KnowledgeMarkdown';
import {
    ResponsiveModal,
    ResponsiveModalBody,
    ResponsiveModalContent,
    ResponsiveModalDescription,
    ResponsiveModalHeader,
    ResponsiveModalTitle,
    ResponsiveModalTrigger,
} from '@/components/ui/responsive-modal';
import { Button } from '@/components/ui/shadcn/button';
import { useTranslations } from '@/hooks/useTranslations';
import { cn } from '@/lib/utils';
import { router } from '@inertiajs/react';
import axios from 'axios';
import { format } from 'date-fns';
import { History, RotateCcw } from 'lucide-react';
import { useState } from 'react';

interface RevisionSummary {
    id: string;
    version: number;
    title: string;
    editor: string | null;
    created_at: string | null;
}

interface RevisionDetail extends RevisionSummary {
    summary: string | null;
    body_markdown: string;
}

interface RevisionHistoryModalProps {
    articleId: string;
}

export default function RevisionHistoryModal({ articleId }: RevisionHistoryModalProps) {
    const { t } = useTranslations();
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [revisions, setRevisions] = useState<RevisionSummary[]>([]);
    const [selected, setSelected] = useState<RevisionDetail | null>(null);
    const [confirmingRestore, setConfirmingRestore] = useState(false);

    const loadRevisions = () => {
        setLoading(true);
        setError(false);
        axios
            .get(route('knowledge-base.revisions', articleId))
            .then((response) => setRevisions(response.data.revisions ?? []))
            .catch(() => setError(true))
            .finally(() => setLoading(false));
    };

    const handleOpenChange = (next: boolean) => {
        setOpen(next);
        if (next) {
            setSelected(null);
            setConfirmingRestore(false);
            loadRevisions();
        }
    };

    const selectRevision = (version: number) => {
        setConfirmingRestore(false);
        axios
            .get(route('knowledge-base.revisions.show', [articleId, version]))
            .then((response) => setSelected(response.data.revision))
            .catch(() => setError(true));
    };

    const restore = (version: number) => {
        router.post(route('knowledge-base.revisions.restore', [articleId, version]));
    };

    const formatTimestamp = (value: string | null): string => (value ? format(new Date(value), 'yyyy-MM-dd HH:mm') : '—');

    return (
        <ResponsiveModal open={open} onOpenChange={handleOpenChange} variant="sheet">
            <ResponsiveModalTrigger asChild>
                <Button variant="outline" size="sm">
                    <History className="h-3.5 w-3.5" />
                    {t('knowledge_base.show.actions.history', 'Version history')}
                </Button>
            </ResponsiveModalTrigger>
            <ResponsiveModalContent size="2xl">
                <ResponsiveModalHeader>
                    <ResponsiveModalTitle>{t('knowledge_base.show.revisions.title', 'Version history')}</ResponsiveModalTitle>
                    <ResponsiveModalDescription>
                        {t(
                            'knowledge_base.show.revisions.description',
                            'Previously saved versions of this article. Restoring replaces the current content and is itself saved as a new version.',
                        )}
                    </ResponsiveModalDescription>
                </ResponsiveModalHeader>

                <ResponsiveModalBody className="grid grid-cols-1 gap-4 md:grid-cols-[260px_1fr]">
                    <div className="max-h-[60vh] overflow-y-auto rounded-lg border">
                        {loading ? (
                            <p className="text-muted-foreground p-4 text-sm">{t('knowledge_base.show.revisions.loading', 'Loading…')}</p>
                        ) : error ? (
                            <p className="text-danger-subtle-foreground p-4 text-sm">
                                {t('knowledge_base.show.revisions.load_error', 'Could not load version history.')}
                            </p>
                        ) : revisions.length === 0 ? (
                            <p className="text-muted-foreground p-4 text-sm">
                                {t('knowledge_base.show.revisions.empty', 'No previous versions yet.')}
                            </p>
                        ) : (
                            <ul className="divide-y">
                                {revisions.map((revision, index) => (
                                    <li key={revision.id}>
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            onClick={() => selectRevision(revision.version)}
                                            className={cn(
                                                'h-auto w-full flex-col items-start justify-start gap-0.5 rounded-none px-3 py-2 text-left font-normal whitespace-normal',
                                                selected?.version === revision.version && 'bg-accent',
                                            )}
                                        >
                                            <span className="flex items-center gap-2 text-sm font-medium">
                                                {t('knowledge_base.show.revisions.version', 'Version :num').replace(':num', String(revision.version))}
                                                {index === 0 && (
                                                    <span className="bg-info-subtle text-info-subtle-foreground rounded px-1.5 py-0.5 text-xs">
                                                        {t('knowledge_base.show.revisions.current', 'Current')}
                                                    </span>
                                                )}
                                            </span>
                                            <span className="text-muted-foreground block text-xs font-normal">
                                                {revision.editor ?? t('knowledge_base.show.revisions.system', 'System')} ·{' '}
                                                {formatTimestamp(revision.created_at)}
                                            </span>
                                        </Button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    <div className="max-h-[60vh] overflow-y-auto rounded-lg border p-4">
                        {selected ? (
                            <>
                                <div className="mb-3 flex flex-wrap items-start justify-between gap-2 border-b pb-3">
                                    <div>
                                        <h3 className="text-base font-semibold">{selected.title}</h3>
                                        <p className="text-muted-foreground text-xs">
                                            {(selected.editor ?? t('knowledge_base.show.revisions.system', 'System')) +
                                                ' · ' +
                                                formatTimestamp(selected.created_at)}
                                        </p>
                                    </div>
                                    {!confirmingRestore ? (
                                        <Button size="sm" variant="outline" onClick={() => setConfirmingRestore(true)}>
                                            <RotateCcw className="h-3.5 w-3.5" />
                                            {t('knowledge_base.show.revisions.restore', 'Restore this version')}
                                        </Button>
                                    ) : (
                                        <div className="flex items-center gap-2">
                                            <Button size="sm" onClick={() => restore(selected.version)}>
                                                {t('knowledge_base.show.revisions.restore', 'Restore this version')}
                                            </Button>
                                            <Button size="sm" variant="outline" onClick={() => setConfirmingRestore(false)}>
                                                {t('knowledge_base.show.revisions.close', 'Close')}
                                            </Button>
                                        </div>
                                    )}
                                </div>
                                {confirmingRestore && (
                                    <p className="text-muted-foreground mb-3 text-sm">
                                        {t(
                                            'knowledge_base.show.revisions.restore_confirm',
                                            'Restore the article to version :num? The current content is replaced (and kept as a new version).',
                                        ).replace(':num', String(selected.version))}
                                    </p>
                                )}
                                <KnowledgeMarkdown>{selected.body_markdown}</KnowledgeMarkdown>
                            </>
                        ) : (
                            <p className="text-muted-foreground text-sm">{t('knowledge_base.show.revisions.preview', 'Preview')}</p>
                        )}
                    </div>
                </ResponsiveModalBody>
            </ResponsiveModalContent>
        </ResponsiveModal>
    );
}
